/**
 * BOTANIX ERP — Session & Login Tracker UI
 * File: src/SessionLogs.jsx
 */
import React, { useState } from 'react';
import { useAuth } from './auth/AuthContext';

const C = {
 accent:'#3AAF58', accentLight:'#EAF7EE',
 bg:'#0A1628', surface:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 amber:'#D4840F', amberBg:'rgba(245,158,11,.1)', amberBorder:'#F0D499',
 red:'#EF4444', redBg:'rgba(239,68,68,.1)', redBorder:'#F0B8B8',
 blue:'#FFFFFF', blueBg:'#EAF1FB',
 purple:'#6B48A8', purpleBg:'#F0EBFB',
 dark:'#0F1E14',
};

const ROLE_COLORS = {
 'Super Admin':'#E91E63','Admin':'#3AAF58','HR Admin':'#558B2F',
 'Resort GM':'#2563A8','Marketing':'#E91E63','Sales':'#2563A8',
 'Front Office':'#1A7A7A','Kitchen Manager':'#D4840F','F&B Manager':'#D4840F',
 'Housekeeping Manager':'#6B48A8','Horticulture Manager':'#558B2F',
 'Activity Manager':'#1A7A7A','Construction Manager':'#C83333',
 'Accounts':'#6B48A8','Purchase Manager':'#D4840F','Store Manager':'#2563A8',
};

const Card = ({children,style={}}) => <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,.05)',...style}}>{children}</div>;
const SH = ({title,icon,color,bg,right}) => (
 <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 18px',borderBottom:`1px solid ${C.border}`,background:bg||'#F5F8F5',borderRadius:'12px 12px 0 0'}}>
 <div style={{display:'flex',alignItems:'center',gap:8}}><span style={{fontSize:18}}>{icon}</span><span style={{fontSize:14,fontWeight:700,color:color||C.text}}>{title}</span></div>
 {right}
 </div>
);
const Th = ({c}) => <th style={{padding:'10px 14px',fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase',letterSpacing:'.06em',textAlign:'left',background:'#F5F8F5',borderBottom:`1px solid ${C.border}`,whiteSpace:'nowrap'}}>{c}</th>;
const Td = ({children,style={}}) => <td style={{padding:'11px 14px',fontSize:13,borderBottom:`1px solid ${C.border}`,verticalAlign:'middle',...style}}>{children}</td>;
const KPI = ({label,value,icon,color,bg,sub}) => (
 <Card style={{padding:16}}>
 <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
 <div style={{fontSize:10,fontWeight:700,color:C.text3,textTransform:'uppercase',letterSpacing:'.07em',lineHeight:1.4}}>{label}</div>
 <div style={{width:30,height:30,borderRadius:8,background:bg||C.accentLight,display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,flexShrink:0}}>{icon}</div>
 </div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:20,fontWeight:800,color:color||C.text}}>{value}</div>
 {sub&&<div style={{fontSize:11,color:C.text3,marginTop:3}}>{sub}</div>}
 </Card>
);

function formatDuration(mins) {
 if (!mins && mins !== 0) return '—';
 if (mins < 1) return 'Less than 1 min';
 if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''}`;
 const h = Math.floor(mins / 60);
 const m = mins % 60;
 return `${h}h ${m}m`;
}

function formatDateTime(iso) {
 if (!iso) return '—';
 try {
 const d = new Date(iso);
 return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
 } catch { return iso; }
}

// Demo data for display when no real sessions yet
const DEMO_SESSIONS = [
 {id:1,userId:1,userName:'Atul Sharma',userRole:'Super Admin',userEmail:'atul@botanix.in',userDept:'Management',loginAt:'2026-07-03T09:00:00',logoutAt:'2026-07-03T12:30:00',durationMins:210,browser:'Chrome',device:'Desktop',os:'Windows',ipAddress:'192.168.1.5',status:'Ended'},
 {id:2,userId:2,userName:'Rajesh Sharma',userRole:'Admin',userEmail:'rajesh@botanix.in',userDept:'Management',loginAt:'2026-07-03T08:45:00',logoutAt:'2026-07-03T18:00:00',durationMins:555,browser:'Chrome',device:'Desktop',os:'Windows',ipAddress:'192.168.1.6',status:'Ended'},
 {id:3,userId:3,userName:'Pooja Rawat',userRole:'Sales',userEmail:'pooja@botanix.in',userDept:'Sales & CRM',loginAt:'2026-07-03T09:15:00',logoutAt:null,durationMins:null,browser:'Chrome',device:'Desktop',os:'Windows',ipAddress:'192.168.1.12',status:'Active'},
 {id:4,userId:7,userName:'Suresh Kumar',userRole:'Kitchen Manager',userEmail:'suresh@botanix.in',userDept:'Kitchen',loginAt:'2026-07-03T06:00:00',logoutAt:'2026-07-03T14:30:00',durationMins:510,browser:'Safari',device:'Mobile',os:'iOS',ipAddress:'192.168.1.18',status:'Ended'},
 {id:5,userId:8,userName:'Deepak Chauhan',userRole:'Housekeeping Manager',userEmail:'deepak@botanix.in',userDept:'Housekeeping',loginAt:'2026-07-03T07:00:00',logoutAt:'2026-07-03T15:00:00',durationMins:480,browser:'Chrome',device:'Mobile',os:'Android',ipAddress:'192.168.1.14',status:'Ended'},
 {id:6,userId:9,userName:'Ramesh Yadav',userRole:'Horticulture Manager',userEmail:'ramesh@botanix.in',userDept:'Horticulture',loginAt:'2026-07-02T16:00:00',logoutAt:'2026-07-02T17:30:00',durationMins:90,browser:'Chrome',device:'Desktop',os:'Windows',ipAddress:'192.168.1.31',status:'Ended'},
 {id:7,userId:11,userName:'Kavita Joshi',userRole:'Accounts',userEmail:'kavita@botanix.in',userDept:'Finance',loginAt:'2026-07-03T09:30:00',logoutAt:null,durationMins:null,browser:'Chrome',device:'Desktop',os:'Windows',ipAddress:'192.168.1.22',status:'Active'},
 {id:8,userId:2,userName:'Rajesh Sharma',userRole:'Admin',userEmail:'rajesh@botanix.in',userDept:'Management',loginAt:'2026-07-02T08:00:00',logoutAt:'2026-07-02T19:00:00',durationMins:660,browser:'Chrome',device:'Desktop',os:'Windows',ipAddress:'192.168.1.6',status:'Ended'},
 {id:9,userId:3,userName:'Pooja Rawat',userRole:'Sales',userEmail:'pooja@botanix.in',userDept:'Sales & CRM',loginAt:'2026-07-02T09:00:00',logoutAt:'2026-07-02T18:00:00',durationMins:540,browser:'Firefox',device:'Desktop',os:'Windows',ipAddress:'192.168.1.12',status:'Ended'},
 {id:10,userId:6,userName:'Priya Singh',userRole:'Marketing',userEmail:'priya@botanix.in',userDept:'Marketing',loginAt:'2026-07-02T10:00:00',logoutAt:'2026-07-02T16:30:00',durationMins:390,browser:'Chrome',device:'Mobile',os:'Android',ipAddress:'192.168.1.35',status:'Ended'},
];

export default function SessionLogs() {
 const { sessionLog, user, isSuperAdmin, isAdmin } = useAuth();
 const [filterUser, setFilterUser] = useState('All');
 const [filterDate, setFilterDate] = useState('');
 const [filterStatus, setFilterStatus] = useState('All');

 // Combine real sessions with demo data
 const allSessions = [...(sessionLog || []), ...DEMO_SESSIONS].sort(
 (a, b) => new Date(b.loginAt) - new Date(a.loginAt)
 );

 const activeSessions = allSessions.filter(s => s.status === 'Active');
 const todaySessions = allSessions.filter(s => s.loginAt?.startsWith(new Date().toISOString().slice(0, 10)));
 const avgDuration = Math.round(allSessions.filter(s => s.durationMins).reduce((sum, s) => sum + s.durationMins, 0) / Math.max(allSessions.filter(s => s.durationMins).length, 1));

 const uniqueUsers = ['All', ...new Set(allSessions.map(s => s.userName))];

 const filtered = allSessions.filter(s =>
 (filterUser === 'All' || s.userName === filterUser) &&
 (filterStatus === 'All' || s.status === filterStatus) &&
 (!filterDate || s.loginAt?.startsWith(filterDate))
 );

 return (
 <div style={{ fontFamily: 'Inter,sans-serif', color: C.text }}>
 {/* Header */}
 <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '0 28px', position: 'sticky', top: 0, zIndex: 50 }}>
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62 }}>
 <div>
 <div style={{ fontSize: 11, color: C.text3, fontWeight: 500, marginBottom: 1 }}>Security & Administration</div>
 <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 20, fontWeight: 800, color: C.text }}>Login & Session Tracker</div>
 </div>
 <div style={{ display: 'flex', gap: 10 }}>
 {activeSessions.length > 0 && (
 <div style={{ padding: '7px 14px', background: C.accentLight, borderRadius: 9 }}>
 <span style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>{activeSessions.length} Users Currently Online</span>
 </div>
 )}
 </div>
 </div>
 </div>

 <div style={{ padding: '24px 28px' }}>

 {/* Security notice */}
 <div style={{ padding: '12px 18px', background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 10, marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
 <span style={{ fontSize: 18 }}></span>
 <span style={{ fontSize: 13, color: C.red, fontWeight: 500 }}>
 All login sessions are tracked with timestamp, device, browser and IP address to prevent unauthorised access and data theft.
 Only Super Admin and Admin can view this log.
 </span>
 </div>

 {/* KPIs */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
 <KPI label="Active Sessions Now" value={activeSessions.length} icon="" color={C.accent} sub="Currently logged in"/>
 <KPI label="Logins Today" value={todaySessions.length} icon="" color={C.blue} bg={C.blueBg}/>
 <KPI label="Total Sessions" value={allSessions.length} icon="" color={C.text}/>
 <KPI label="Avg Session Duration" value={formatDuration(avgDuration)} icon="" color={C.amber} bg={C.amberBg}/>
 <KPI label="Unique Users Today" value={new Set(todaySessions.map(s => s.userId)).size} icon="" color={C.purple} bg={C.purpleBg}/>
 </div>

 {/* Active sessions highlight */}
 {activeSessions.length > 0 && (
 <Card style={{ marginBottom: 16 }}>
 <SH title="Users Currently Online" icon="" color={C.accent} bg={C.accentLight}/>
 <div style={{ padding: '14px 18px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
 {activeSessions.map((s, i) => {
 const roleColor = ROLE_COLORS[s.userRole] || C.text3;
 const loginMins = Math.round((Date.now() - new Date(s.loginAt)) / 60000);
 return (
 <div key={i} style={{ padding: '12px 16px', background: C.bg, borderRadius: 10, border: `1px solid ${C.accent}44`, minWidth: 200 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
 <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.accent }} />
 <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.userName}</span>
 </div>
 <div style={{ fontSize: 11, color: roleColor, fontWeight: 600, marginBottom: 4 }}>{s.userRole}</div>
 <div style={{ fontSize: 11, color: C.text3 }}>Since {formatDateTime(s.loginAt)}</div>
 <div style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>Online for {formatDuration(loginMins)}</div>
 <div style={{ fontSize: 11, color: C.text3 }}>{s.device} · {s.browser} · {s.ipAddress}</div>
 </div>
 );
 })}
 </div>
 </Card>
 )}

 {/* Filters */}
 <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
 <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
 style={{ padding: '8px 12px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 9, color: C.text, fontSize: 13, fontFamily: 'Inter,sans-serif' }}>
 {uniqueUsers.map(u => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={u}>{u}</option>)}
 </select>
 <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
 style={{ padding: '8px 12px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 9, color: C.text, fontSize: 13, fontFamily: 'Inter,sans-serif' }}>
 {['All', 'Active', 'Ended'].map(s => <option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
 style={{ padding: '8px 12px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 9, color: C.text, fontSize: 13, fontFamily: 'Inter,sans-serif' }} />
 {filterDate && <button onClick={() => setFilterDate('')} style={{ padding: '8px 12px', background: C.bg, border: `1px solid ${C.border2}`, borderRadius: 9, cursor: 'pointer', color: C.text3, fontSize: 12 }}>Clear</button>}
 <div style={{ marginLeft: 'auto', fontSize: 12, color: C.text3 }}>{filtered.length} session{filtered.length !== 1 ? 's' : ''} found</div>
 </div>

 {/* Session table */}
 <Card>
 <SH title="All Login Sessions" icon="" color={C.blue} bg={C.blueBg}
 right={<span style={{ fontSize: 11, color: C.text3 }}>Showing last 500 sessions</span>} />
 <div style={{ overflowX: 'auto' }}>
 <table style={{ width: '100%', borderCollapse: 'collapse' }}>
 <thead>
 <tr>
 <Th c="User"/>
 <Th c="Role"/>
 <Th c="Login Time"/>
 <Th c="Logout Time"/>
 <Th c="Duration"/>
 <Th c="Device"/>
 <Th c="Browser"/>
 <Th c="OS"/>
 <Th c="IP Address"/>
 <Th c="Status"/>
 </tr>
 </thead>
 <tbody>
 {filtered.map((s, i) => {
 const roleColor = ROLE_COLORS[s.userRole] || C.text3;
 const isCurrentUser = s.userEmail === user?.email;
 return (
 <tr key={s.id || i} style={{ borderBottom: `1px solid ${C.border}`, background: s.status === 'Active' ? '#F5FFF8' : isCurrentUser ? '#F5F9FF' : C.surface }}>
 <Td>
 <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.userName}{isCurrentUser && <span style={{ fontSize: 10, color: C.accent, marginLeft: 6 }}>YOU</span>}</div>
 <div style={{ fontSize: 11, color: C.text3 }}>{s.userEmail}</div>
 </Td>
 <Td>
 <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: roleColor, background: roleColor + '15' }}>{s.userRole}</span>
 </Td>
 <Td style={{ fontSize: 12, color: C.text2, whiteSpace: 'nowrap' }}>{formatDateTime(s.loginAt)}</Td>
 <Td style={{ fontSize: 12, color: C.text3, whiteSpace: 'nowrap' }}>{s.logoutAt ? formatDateTime(s.logoutAt) : <span style={{ color: C.accent, fontWeight: 600 }}>Still online</span>}</Td>
 <Td>
 <span style={{ fontWeight: 600, color: s.durationMins > 480 ? C.red : s.durationMins > 240 ? C.amber : C.text }}>
 {s.durationMins !== null ? formatDuration(s.durationMins) : <span style={{ color: C.accent }}>Active</span>}
 </span>
 </Td>
 <Td>
 <span style={{ fontSize: 12 }}>
 {s.device === 'Mobile' ? '' : s.device === 'Tablet' ? '' : ''} {s.device}
 </span>
 </Td>
 <Td style={{ fontSize: 12, color: C.text3 }}>{s.browser}</Td>
 <Td style={{ fontSize: 12, color: C.text3 }}>{s.os}</Td>
 <Td style={{ fontFamily: 'monospace', fontSize: 12, color: C.text3 }}>{s.ipAddress}</Td>
 <Td>
 <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: s.status === 'Active' ? C.accent : C.text3, background: s.status === 'Active' ? C.accentLight : C.border }}>
 {s.status}
 </span>
 </Td>
 </tr>
 );
 })}
 {filtered.length === 0 && (
 <tr><td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: C.text3 }}>No sessions found for selected filters</td></tr>
 )}
 </tbody>
 </table>
 </div>
 </Card>

 {/* Per user summary */}
 <Card style={{ marginTop: 16 }}>
 <SH title="Per-User Activity Summary" icon="" color={C.purple} bg={C.purpleBg} />
 <div style={{ padding: '14px 18px' }}>
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
 {[...new Set(allSessions.map(s => s.userName))].map(name => {
 const userSessions = allSessions.filter(s => s.userName === name);
 const totalMins = userSessions.filter(s => s.durationMins).reduce((sum, s) => sum + s.durationMins, 0);
 const lastLogin = userSessions[0];
 const role = lastLogin?.userRole;
 const roleColor = ROLE_COLORS[role] || C.text3;
 const avatar = name.split(' ').map(w => w[0]).join('').slice(0, 2);
 return (
 <div key={name} style={{ padding: '14px', background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
 <div style={{ width: 36, height: 36, borderRadius: 10, background: roleColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: roleColor }}>{avatar}</div>
 <div>
 <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{name}</div>
 <div style={{ fontSize: 11, color: roleColor }}>{role}</div>
 </div>
 </div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
 {[['Sessions', userSessions.length], ['Total Time', formatDuration(totalMins)], ['Last Login', lastLogin?.loginAt?.slice(0, 10) || '—'], ['Device', lastLogin?.device || '—']].map(([k, v]) => (
 <div key={k} style={{ padding: '6px 8px', background: C.surface, borderRadius: 7, border: `1px solid ${C.border}` }}>
 <div style={{ fontSize: 9, color: C.text3, textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
 <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{v}</div>
 </div>
 ))}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 </Card>
 </div>
 </div>
 );
}
